import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/authContext";
import { api } from "@/lib/api";
import { UserPlus, ShieldAlert, Mail } from "lucide-react";

export default function InviteTeam() {
  const { user } = useAuth();
  const { toast } = useToast();

  const [form, setForm] = useState({ name: "", email: "", role: "staff" as "staff" | "contractor" | "manager" | "admin" });

  const canInvite = user?.role === "admin" || user?.role === "manager";

  const inviteMutation = useMutation({
    mutationFn: () => api.inviteTeamMember(form),
    onSuccess: () => {
      toast({
        title: "Invite sent",
        description: `An invite email was sent to ${form.email}. They'll set their password on first login.`,
      });
      setForm({ name: "", email: "", role: "staff" as "staff" | "contractor" | "manager" | "admin" });
    },
    onError: (err: Error) => {
      toast({ title: "Invite failed", description: err.message, variant: "destructive" });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    inviteMutation.mutate();
  };

  if (!canInvite) {
    return (
      <Layout>
        <div className="space-y-6">
          <h1 className="text-3xl font-heading font-bold">Invite Team</h1>
          <Card className="border-dashed border-2 border-destructive/50">
            <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <ShieldAlert className="w-12 h-12 mb-4 text-destructive/50" />
              <p className="text-lg font-medium">Access Denied</p>
              <p className="text-sm">Only admins and managers can invite team members.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-lg">
        <div className="flex items-center gap-3">
          <UserPlus className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-heading font-bold">Invite Team</h1>
            <p className="text-muted-foreground">Add a new staff member to Central Command</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Send invite</CardTitle>
            <CardDescription>
              They'll receive an email with a link to set their password and log in.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full name <span className="text-destructive">*</span></Label>
                <Input
                  id="name"
                  placeholder="Jane Smith"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email address <span className="text-destructive">*</span></Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="jane@carpentercpas.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select
                  value={form.role}
                  onValueChange={(v) => setForm({ ...form, role: v as "staff" | "contractor" | "manager" | "admin" })}
                >
                  <SelectTrigger id="role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff — time entry, own projects</SelectItem>
                    <SelectItem value="contractor">Contractor — same as staff, no financials or reports</SelectItem>
                    <SelectItem value="manager">Manager — approve timesheets, all reports</SelectItem>
                    {user?.role === "admin" && (
                      <SelectItem value="admin">Admin — full access</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <Button type="submit" className="w-full" disabled={inviteMutation.isPending}>
                <Mail className="w-4 h-4 mr-2" />
                {inviteMutation.isPending ? "Sending invite..." : "Send invite"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
